import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Button, Drawer, Typography } from "antd";
import CustomList from "../../../../components/List";
import ListItem from "../../../../components/ListItem";
import CustomPagination from "../../../../components/CustomPagination";
import useProcuringEntityPackage from "../../../../../hooks/useProcuringEntityPackage";
import { useReportDateRange } from "../../../../../hooks/useReportDateRange";
import { useMaterialTestingApprovals } from "../../../../../hooks/useDataHooks";
import ActionBar from "../../../../components/ActionBar";

const material_mobilization_status = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 0, xs: 0 };
const sub_project = { xxl: 5, xl: 5, lg: 5, md: 5, sm: 12, xs: 12 };
const material_source = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 12, xs: 12 };
const material_quantity_mobilized = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 0, xs: 0 };
const material_description = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 12, xs: 12 };
const remarks = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 0, xs: 0 };

const headerLayout = [
  { ...sub_project, header: "Subproject" },
  { ...material_description, header: "Material Description" },
  { ...material_source, header: "Material Source" },
  { ...material_quantity_mobilized, header: "Quantity Mobilized" },
  { ...material_mobilization_status, header: "Mobilization (%)" },
  { ...remarks, header: "Remarks" },
];

const MaterialMobilizedTesting = ({ match }) => {
  const [activeItem, setActiveItem] = useState(null);
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const handleRowClick = (item) => {
    setActiveItem(item);
    setVisible(true);
  };

  const handleCloseDrawer = () => {
    setVisible(false);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    setActiveItem(null);
  };

  const {
    params: { packageId },
  } = match;
  const { startDate, endDate } = useReportDateRange();
  
  const { socialSafeguards: materialsData, loading, error } = useMaterialTestingApprovals(
    packageId,
    startDate,
    endDate
  );

  useEffect(() => {
  }, [packageId, startDate, endDate, materialsData, loading, error]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const history = useHistory();
  const { procuringEntityPackage } = useProcuringEntityPackage(packageId);
  const packageName = procuringEntityPackage?.name || "";

  const formattedMaterialsData = materialsData?.map((material) => ({
    ...material,
    report_period: `${material.report_start_date || '-'} to ${material.report_end_date || '-'}`,
    material_mobilization_status: `${material.material_mobilization_status || 0}%`,
  })) || [];

  const pagination = {
    current_page: currentPage,
    per_page: pageSize,
    total: formattedMaterialsData.length,
    last_page: Math.ceil(formattedMaterialsData.length / pageSize),
    from: (currentPage - 1) * pageSize + 1,
    to: Math.min(currentPage * pageSize, formattedMaterialsData.length),
    has_more_pages: currentPage < Math.ceil(formattedMaterialsData.length / pageSize),
    next_page_url: null,
    prev_page_url: null
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = formattedMaterialsData.slice(startIndex, endIndex);

  if (error) {
    console.error("Error loading material testing approvals:", error);
  }

  return (
    <>
      <div style={{ padding: "16px" }}>
      {error && (
          <div style={{ 
            margin: "16px 0", 
            padding: "16px", 
            backgroundColor: "#fff2f0", 
            border: "1px solid #ffccc7", 
            borderRadius: "6px",
            color: "#cf1322"
          }}>
            <strong>Error:</strong> Failed to load material testing approvals. Please try again later.
          </div>
        )}
              
        {!loading && !error && formattedMaterialsData.length === 0 && (
          <div style={{ 
            margin: "16px 0", 
            padding: "16px", 
            backgroundColor: "#f6ffed", 
            border: "1px solid #b7eb8f", 
            borderRadius: "6px",
            color: "#389e0d",
            textAlign: "center"
          }}>
            <strong>No Data Available:</strong> No material testing approvals found for the selected package and date range.
          </div>
        )}
        

        <ActionBar actionButtonProp={{
          title: `${packageName || "==//=="}: Material Mobilized, Testing & Approval`,
          arrActions: [],
          buttons: [],
        }} />
  
        <div className="prst-table">
        <CustomList
          itemName="Material Mobilization"
          showToolbar={false}
          items={paginatedData}
          page={currentPage}
          itemCount={pagination?.total || 0}
          loading={loading}
          headerLayout={headerLayout}
          renderListItem={({ item }) => (
            <ListItem
              key={item.id} // eslint-disable-line
              name={item?.sub_project || "==//=="}
              item={item}
              isPackage={false}
              renderActions={() => false}
              onClick={() => handleRowClick(item)}
              style={{
                backgroundColor:
                  activeItem?.id === item.id
                    ? "#f0f0f0"
                    : "transparent",
              }}
            >
              <Col 
                {...sub_project} 
                className="contentEllipse"
                data-label="Subproject"
              >
                {item?.sub_project?.length > 20 ? item?.sub_project.slice(0, 20) + "..." : item?.sub_project || "==//=="}
              </Col>
              
              <Col 
                {...material_description} 
                className="contentEllipse"
                data-label="Material Description"
              >
                {item?.material_description?.length > 20 ? item?.material_description.slice(0, 20) + "..." : item?.material_description || "==//=="}
              </Col>
              
              <Col 
                {...material_source} 
                className="contentEllipse"
                data-label="Material Source"
              >
                {item?.material_source?.length > 20 ? item?.material_source.slice(0, 20) + "..." : item?.material_source || "==//=="}
              </Col>
              
              <Col 
                {...material_quantity_mobilized} 
                className="contentEllipse"
                data-label="Quantity Mobilized"
              >
                {item?.material_quantity_mobilized || "-"}
              </Col>
              
              <Col
                {...material_mobilization_status}
                className="contentEllipse"
                data-label="Mobilization (%)"
                title={item?.material_mobilization_status || "==//=="}
              >
                {item?.material_mobilization_status || "==//=="}
              </Col>  
              
              <Col 
                {...remarks} 
                className="contentEllipse"
                data-label="Remarks"
              >
                {item?.remarks?.length > 20 ? item?.remarks.slice(0, 20) + "..." : item?.remarks || "==//=="}
              </Col>

              <button 
                className="view-card-details" 
                onClick={() => handleRowClick(item)}
              >
                View Details
              </button>
            </ListItem>
          )}
        />
        </div>
        
        <CustomPagination 
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={loading}
        />

        {activeItem && (
          <Drawer
            title={`Material Mobilized Testing Details`}
            placement="right"
            closable={true}
            onClose={handleCloseDrawer}
            visible={visible}
            width={550}
            style={{
              zIndex: 9999,
            }}
          >
            <p>
              <strong>Subproject:</strong> {activeItem.sub_project || "==//=="}
            </p>
            <p>
              <strong>Material Description:</strong>{" "}
              {activeItem.material_description || "==//=="}
            </p>
            <p>
              <strong>Material Source:</strong>{" "}
              {activeItem.material_source || "==//=="}
            </p>
            <p>
              <strong>Quantity Mobilized:</strong>{" "}
              {activeItem.material_quantity_mobilized || "==//=="}
            </p>
            <p>
              <strong>Mobilization Status:</strong>{" "}
              {activeItem.material_mobilization_status || "==//=="}
            </p>
            <p>
              <strong>Report Start Date:</strong>{" "}
              {activeItem.report_start_date || "==//=="}
            </p>
            <p>
              <strong>Report End Date:</strong>{" "}
              {activeItem.report_end_date || "==//=="}
            </p>
            <p>
              <strong>Remarks:</strong> {activeItem.remarks || "==//=="}
            </p>
          </Drawer>
        )}
      </div>
    </>
  );
};

export default MaterialMobilizedTesting;
